import { toNumber } from "#/lib/formatters";

type CrossEquityParams = {
	/** `clearinghouseState.crossMarginSummary.accountValue`. */
	crossMarginAccountValue: string | number | null | undefined;
	/** `clearinghouseState.marginSummary.accountValue` — fallback only. */
	marginAccountValue: string | number | null | undefined;
	/** Spot USDC `total` (unified accounts share this pool with Perp). */
	spotUsdc: number;
	/** Spot USDC `hold` — the slice already counted as position margin. */
	spotUsdcHold: number;
	/** `userAbstraction === "unifiedAccount"`. */
	isUnified: boolean;
};

/**
 * Account equity backing a **Cross** position — the `account_value` term of
 * Hyperliquid's liquidation formula, fed to `estimateLiquidationPrice`.
 *
 * Deliberately not `useTradingBalance()`, which answers a different question
 * ("what can this user spend?") and so is anchored on `marginSummary` and counts
 * spot for dexAbstraction accounts. Liquidation asks what still backs the
 * position once the mark moves against it:
 *
 *   • **`crossMarginSummary`**, not `marginSummary`: an isolated position's
 *     margin and PnL never backstop a Cross position, and `marginSummary`
 *     includes them. The two are equal when no isolated position is open.
 *   • **Spot USDC only when unified**, where it is literally the same collateral
 *     pool (`− hold` avoids double-counting the slice already posted as margin —
 *     see ACCOUNT.md, which verifies this shape against both HL accounting
 *     models). A dexAbstraction account's spot sits in a separate pool pulled in
 *     at order time, not at liquidation time; counting it would overstate the
 *     cushion, and every error here must fail toward showing *more* risk.
 *
 * Returns 0 when equity is unknown or non-positive — `estimateLiquidationPrice`
 * reads that as "Cross equity not loaded" and shows its indicative estimate.
 */
export function computeCrossEquity({
	crossMarginAccountValue,
	marginAccountValue,
	spotUsdc,
	spotUsdcHold,
	isUnified,
}: CrossEquityParams): number {
	const crossAccountValue = toNumber(
		crossMarginAccountValue ?? marginAccountValue,
	);
	const equity = isUnified
		? crossAccountValue + spotUsdc - spotUsdcHold
		: crossAccountValue;
	return equity > 0 ? equity : 0;
}
